import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  BarChart3,
  Check,
  CircleSlash,
  Loader2,
  Search,
} from '@lognai/synapse-design-system-v2/icons';
import {
  Badge,
  Button,
  Dialog,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lognai/synapse-design-system-v2';
import { HighchartsRenderer } from './HighchartsRenderer';
import { executeVisualBatch, listVisuals, resolveChartTheme, type BatchResult } from './api';
import type { ChartThemePayload, SemanticFilter, VisualDefinition } from '@lognai/plugin-analytics';
import { userFacingAnalyticsError } from '../lib/user-facing';
import './visual-library-picker.css';

const DOMAINS = ['orders', 'inventory', 'warehouse', 'shipments', 'procurement', 'products'];

/** The library exposes ~70 charts; fetch the whole set so none are hidden. */
const PAGE_SIZE = 100;

/** Charts executed per batch request as cards scroll into view. */
const PREVIEW_BATCH = 8;

const businessLabel = (value: string) =>
  value.replace(/[_-]/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());

/**
 * One library card. Reports itself visible so its preview is only executed when
 * it actually scrolls into view — executing all ~70 at once would hammer Cube.
 */
function PickerCard({
  visual,
  execution,
  added,
  theme,
  disabled,
  onVisible,
  onSelect,
}: {
  visual: VisualDefinition;
  execution?: BatchResult;
  added: boolean;
  theme?: ChartThemePayload;
  disabled?: boolean;
  onVisible: () => void;
  onSelect: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (!('IntersectionObserver' in window)) {
      onVisible();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onVisible();
          observer.disconnect();
        }
      },
      { rootMargin: '160px' }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [onVisible]);

  const rowState = execution?.result?.meta?.rowState;

  return (
    <div
      ref={ref}
      className={`group relative flex min-h-[210px] flex-col overflow-hidden rounded-lg border border-border bg-surface text-left transition hover:border-primary/40 ${added || disabled ? 'opacity-60' : ''}`}
    >
      <button
        type="button"
        disabled={added || disabled}
        onClick={onSelect}
        aria-label={`${added ? 'Added' : 'Add'} ${visual.title} chart`}
        className="absolute inset-0 z-10 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
      />
      <div
        aria-hidden="true"
        {...({ inert: '' } as Record<string, string>)}
        className="pointer-events-none h-[112px] shrink-0 overflow-hidden border-b border-border bg-muted/10 p-1"
      >
        {execution?.result && rowState === 'ready' ? (
          <HighchartsRenderer visual={execution.result.visual} height={104} compact theme={theme} />
        ) : rowState === 'zero' ? (
          <div className="grid h-full place-items-center text-center">
            <div>
              <div className="mx-auto grid h-7 w-7 place-items-center rounded-md bg-muted text-muted-foreground">
                <CircleSlash size={14} />
              </div>
              <p className="mt-1.5 text-[11px] font-medium">Zero for this period</p>
            </div>
          </div>
        ) : rowState === 'empty' ? (
          <div className="grid h-full place-items-center px-4 text-center text-[11px] text-muted-foreground">
            No data for the current company
          </div>
        ) : execution && !execution.ok ? (
          <div className="grid h-full place-items-center px-4 text-center text-[11px] text-muted-foreground">
            This chart could not be loaded
          </div>
        ) : (
          <div className="grid h-full place-items-center">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 text-[13px] font-semibold leading-4 text-foreground">
            {visual.title}
          </h3>
          {added && (
            <Badge variant="secondary" className="shrink-0">
              <Check size={11} /> Added
            </Badge>
          )}
        </div>
        <p className="mt-1 line-clamp-2 text-[11px] leading-4 text-muted-foreground">
          {visual.businessQuestion}
        </p>
        <div className="mt-auto flex flex-wrap gap-1 pt-2">
          {visual.domains.slice(0, 2).map((item: string) => (
            <Badge key={item} variant="outline" className="text-[10px]">
              {businessLabel(item)}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Picks a saved analytics chart for a workspace or dashboard.
 *
 * Mirrors the Analytics chart library: the same governed list, the same live
 * previews, so what a user picks here is what they saw there.
 */
export function ChartLibraryPicker({
  open,
  onOpenChange,
  onSelect,
  alreadyAdded,
  theme: suppliedTheme,
  filters,
  dateRange,
  disabled = false,
  replacing = false,
  scopes,
  filtersForVisual,
  headerAction,
  content,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (visual: VisualDefinition) => void;
  alreadyAdded: Set<string>;
  theme?: ChartThemePayload;
  filters?: SemanticFilter[];
  dateRange?: [string, string];
  disabled?: boolean;
  replacing?: boolean;
  scopes?: Array<'system' | 'company' | 'private'>;
  filtersForVisual?: (visualId: string) => SemanticFilter[];
  /** Extra control under the title, e.g. the workspace's "Create with AI". */
  headerAction?: ReactNode;
  /** Shown in place of the library, inside the same dialog (the workspace's AI creator). */
  content?: ReactNode;
}) {
  const [query, setQuery] = useState('');
  const [domain, setDomain] = useState('all');
  const [items, setItems] = useState<VisualDefinition[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [retry, setRetry] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [total, setTotal] = useState(0);
  const generation = useRef(0);
  const previewBusy = useRef(false);
  const [previewTick, setPreviewTick] = useState(0);
  const [theme, setTheme] = useState<ChartThemePayload>();
  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());
  const [previews, setPreviews] = useState<Record<string, BatchResult>>({});
  const inFlight = useRef(new Set<string>());

  useEffect(() => {
    if (!open || suppliedTheme) return;
    resolveChartTheme()
      .then((resolved) => setTheme(resolved.payload))
      .catch(() => undefined);
  }, [open, suppliedTheme]);

  useEffect(() => {
    if (!open) return;
    let live = true;
    setBusy(true);
    setError(undefined);
    const timer = window.setTimeout(
      () => {
        listVisuals({
          q: query || undefined,
          domains: domain === 'all' ? undefined : [domain],
          sort: 'relevance',
          pageSize: PAGE_SIZE,
          page: pageNumber,
          scopes,
        })
          .then((page) => {
            if (!live) return;
            setItems((previous) =>
              pageNumber === 1
                ? page.items
                : [
                    ...previous,
                    ...page.items.filter(
                      (item) => !previous.some((existing) => existing.id === item.id)
                    ),
                  ]
            );
            setTotal(page.total);
            if (pageNumber === 1) setVisibleIds(new Set());
          })
          .catch((cause) => {
            if (live)
              setError(userFacingAnalyticsError(cause, 'The chart library could not be loaded.'));
          })
          .finally(() => {
            if (live) setBusy(false);
          });
      },
      query ? 250 : 0
    );
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [domain, open, query, pageNumber, retry, JSON.stringify(scopes)]);

  const previewContext = JSON.stringify({ filters, dateRange });
  useEffect(() => {
    generation.current++;
    setVisibleIds(new Set());
    setPreviews({});
    inFlight.current.clear();
    previewBusy.current = false;
    if (open) {
      setQuery('');
      setDomain('all');
      setPageNumber(1);
    }
    return () => {
      generation.current++;
    };
  }, [open, previewContext]);

  useEffect(() => {
    if (!open || previewBusy.current) return;
    const ids = [...visibleIds]
      .filter((id) => !previews[id] && !inFlight.current.has(id))
      .slice(0, PREVIEW_BATCH);
    if (!ids.length) return;
    const request = generation.current;
    previewBusy.current = true;
    ids.forEach((id) => inFlight.current.add(id));
    // A batch chain only schedules the next group once this one has settled.
    executeVisualBatch(
      ids.map((visualId) => ({
        visualId,
        filters: filtersForVisual?.(visualId) ?? filters,
        dateRange,
      }))
    )
      .then((batch) => {
        if (request !== generation.current) return;
        setPreviews((current) => ({
          ...current,
          ...Object.fromEntries(
            ids.map((id) => [
              id,
              batch.results.find((result) => result.visualId === id) ?? { visualId: id, ok: false },
            ])
          ),
        }));
      })
      .catch(() => {
        if (request === generation.current)
          setPreviews((current) => ({
            ...current,
            ...Object.fromEntries(ids.map((id) => [id, { visualId: id, ok: false }])),
          }));
      })
      .finally(() => {
        if (request !== generation.current) return;
        ids.forEach((id) => inFlight.current.delete(id));
        previewBusy.current = false;
        setPreviewTick((value) => value + 1);
      });
  }, [open, previews, visibleIds, previewContext, previewTick]);

  const changeQuery = (value: string) => {
    setQuery(value);
    setPageNumber(1);
    setItems([]);
  };
  const changeDomain = (value: string) => {
    setDomain(value);
    setPageNumber(1);
    setItems([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="visual-library-picker h-[88dvh] max-h-[88dvh] w-[95vw] max-w-[1180px] p-0">
        {content ? <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-6">{content}</div> : <>
        <Dialog.Header className="shrink-0 border-b border-border px-5 py-4 pr-12">
          <Dialog.Title>{replacing ? 'Replace chart' : 'Add a chart'}</Dialog.Title>
          <Dialog.Description>
            Pick a saved chart from the analytics library. Previews use the active company's data.
          </Dialog.Description>
        </Dialog.Header>
        {headerAction ? <div className="shrink-0 px-5 pt-3">{headerAction}</div> : null}

        <div className="flex shrink-0 flex-wrap items-center gap-2 px-5 py-3">
          <div className="relative min-w-[220px] flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-2.5 text-muted-foreground"
              size={16}
            />
            <Input
              className="pl-9"
              value={query}
              onChange={(event) => changeQuery(event.target.value)}
              placeholder="Search charts"
              aria-label="Search charts"
            />
          </div>
          <Select value={domain} onValueChange={changeDomain}>
            <SelectTrigger
              className="h-9 w-auto min-w-[170px]"
              aria-label="Filter by business area"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All business areas</SelectItem>
              {DOMAINS.map((item) => (
                <SelectItem key={item} value={item}>
                  {businessLabel(item)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">{total} charts</span>
        </div>

        {error && (
          <div
            role="alert"
            className="mx-5 rounded-lg border border-destructive-border bg-destructive-subtle px-4 py-3 text-sm text-destructive-foreground"
          >
            {error}
            <Button size="xs" variant="ghost" onClick={() => setRetry((value) => value + 1)}>
              Retry library
            </Button>
          </div>
        )}

        <div
          role="region"
          aria-label="Chart library"
          className="min-h-0 flex-1 overflow-y-auto px-5 pb-4"
        >
          {busy && items.length === 0 ? (
            <div className="grid min-h-[280px] place-items-center">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="grid min-h-[280px] place-items-center text-center">
              <div>
                <BarChart3 className="mx-auto text-muted-foreground" />
                <p className="mt-3 text-sm font-medium">No matching charts</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Try a broader search or a different business area.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-3 pb-1">
              {items.map((visual) => (
                <PickerCard
                  key={visual.id}
                  visual={visual}
                  execution={previews[visual.id]}
                  added={alreadyAdded.has(visual.id)}
                  disabled={disabled}
                  theme={suppliedTheme ?? theme}
                  onVisible={() =>
                    setVisibleIds((current) =>
                      current.has(visual.id) ? current : new Set(current).add(visual.id)
                    )
                  }
                  onSelect={() => onSelect(visual)}
                />
              ))}
            </div>
          )}
        </div>

        {items.length < total && (
          <div className="shrink-0 px-5 pb-3">
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !!error}
              isLoading={busy}
              onClick={() => setPageNumber((value) => value + 1)}
            >
              Load more charts
            </Button>
          </div>
        )}
        <Dialog.Footer className="shrink-0 border-t border-border px-5 py-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </Dialog.Footer>
        </>}
      </Dialog.Content>
    </Dialog>
  );
}
